import { Center, Text, VStack, useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import FilledButton from '../components/FilledButton';
import Page from '../components/Page';
import ToastMessageBox from '../components/alerts/ToastMessageBox';
import { isNativeAndroid, isNativeIOS, sendToNative } from '../utils/NativeUtil';
import { trackError } from '../utils/highlightUtils';

const AccountEnableGuideBox = styled.div`
  display: flex;
  width: 327px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 24px;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  word-break: auto-phrase;
`;

export default function UnauthorizedAccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const currentQueryParams = new URLSearchParams(location.search);
  const isBlocked = currentQueryParams.get('blocked') === 'true';
  const isChild = currentQueryParams.get('isChild') === 'true';

  const pageType = isBlocked ? 'blockUserPage' : isChild ? 'childLoginPage' : 'requiredLoginPage';

  const pageTitle = t(`${pageType}.pageName`);
  const titleText = t(`${pageType}.title`);

  const titleEmoji = isBlocked ? '🚫' : isChild ? '👩‍👧‍👦' : '🥺';
  const titleFontSize = isChild ? '26px' : '17px';

  const currentDate = new Date();

  const handleLogin = (isDeleteAccount) => {
    // 이 페이지로 왔다면 로그인이 풀린 상태.. 네이티브는 로그아웃 시킨다.
    initializeLocalStorage();
    if (isDeleteAccount) {
      toast({
        duration: 1500,
        render: () => <ToastMessageBox message={t('childLoginPage.exitPocapantoastMessage')} />
      });
      setTimeout(() => {
        navigate('/onboarding', { replace: true });
      }, 1000);
    } else {
      navigate('/onboarding', { replace: true });
    }
  };

  const initializeLocalStorage = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('artist');
      localStorage.clear();
      if (isNativeAndroid() && window.Android) {
        window.Android?.logout();
      } else {
        sendToNative('{"type": "logout"}');
      }
    } catch (error) {
      trackError(error, 'UnauthorizedAccess', {
        function: 'nativeLogout',
        context: 'UnauthorizedAccessPage',
        isAndroid: isNativeAndroid(),
        isIOS: isNativeIOS(),
        hasAndroidObject: !!(isNativeAndroid() && window.Android),
        message: '네이티브 로그아웃 실패 - 웹 로그아웃은 계속 진행됩니다'
      });
      console.error('네이티브 로그아웃 실패:', error?.message);
      toast({
        duration: 2000,
        isClosable: true,
        render: () => <ToastMessageBox message={t('errorBoundary.title')} />
      });
    }
  };

  return (
    <Page title={pageTitle} showBackButton={false}>
      <Center h={'calc(var(--vh, 1vh) * 90)'}>
        <VStack marginTop={'40px'} className={isChild ? 'text-left' : 'text-center'}>
          <Text fontSize={'50px'}>{titleEmoji}</Text>
          <Text
            className="text-main-light-2 dark:text-main-dark-2"
            fontSize={titleFontSize}
            fontWeight={700}
            wordBreak={'keep-all'}
          >
            {titleText}
          </Text>
          <Text
            className="whitespace-pre-line text-main-light-4 dark:text-main-dark-4"
            fontSize={'14px'}
            fontWeight={400}
            paddingBottom={'20px'}
            wordBreak={'keep-all'}
          >
            {
              <Trans
                i18nKey={`${pageType}.description`}
                components={{ bold: <strong /> }}
                values={{
                  YYYY: currentDate.getFullYear(),
                  MM: currentDate.getMonth() + 1,
                  DD: currentDate.getDate()
                }}
              />
            }
          </Text>
          {isChild && (
            <AccountEnableGuideBox className="text-center bg-surface-light dark:bg-surface-dark text-main-light-2 dark:text-main-dark-2">
              <Text className="whitespace-pre-line" fontSize={'14px'} wordBreak={'keep-all'}>
                <Trans
                  i18nKey={`${pageType}.enableAccountGuide`}
                  components={{ bold: <strong /> }}
                />
              </Text>
            </AccountEnableGuideBox>
          )}
          {!isBlocked && !isChild && (
            <FilledButton
              paddingX={'18px'}
              paddingY={'11px'}
              fontWeight={600}
              fontSize={'14px'}
              text={t('login')}
              onClick={() => handleLogin(false)}
            />
          )}
          {isChild && (
            <VStack spacing={'72px'}>
              <Text
                className="text-warning"
                fontSize={'14px'}
                fontWeight={400}
                as={'u'}
                onClick={() => handleLogin(true)}
              >
                {t('childLoginPage.deleteAccount')}
              </Text>
              <Text
                className="text-primary-light dark:text-primary-dark"
                fontSize={'14px'}
                fontWeight={400}
                as={'u'}
                onClick={() => handleLogin(false)}
              >
                {t('childLoginPage.backToIntro')}
              </Text>
            </VStack>
          )}
        </VStack>
      </Center>
    </Page>
  );
}
